import { View, Text, ScrollView, TouchableOpacity, StyleSheet, ActivityIndicator, Animated } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useRef, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useWorkoutRoutines } from '@/hooks/useWorkoutRoutines';
import { WorkoutCalendar } from '@/components/WorkoutCalendar';
import { NotificationSettings } from '@/components/NotificationSettings';
import { supabase } from '@/lib/supabase';

function formatDateShort(iso: string): string {
  // Data-pura ("YYYY-MM-DD", ex.: start_date/end_date) deve ser lida como data LOCAL.
  // new Date("2026-06-06") interpreta como meia-noite UTC e, em fusos negativos
  // (UTC-3/-4), cai no dia anterior (05/06). Timestamps (lastPerformedAt) seguem em new Date().
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))
    : new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function PulseDot() {
  const scale = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(scale, { toValue: 1.5, duration: 700, useNativeDriver: true }),
        Animated.timing(scale, { toValue: 1,   duration: 700, useNativeDriver: true }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, []);
  return <Animated.View style={[styles.pulseDot, { transform: [{ scale }] }]} />;
}

export default function StudentHomeScreen() {
  const { profile } = useAuth();
  const router = useRouter();
  const [studentId, setStudentId] = useState<string | null>(null);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifHour, setNotifHour] = useState(7);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!profile) return;
    supabase
      .from('students')
      .select('id, notification_enabled, notification_hour')
      .eq('profile_id', profile.id)
      .order('id', { ascending: true })
      .limit(1)
      .then(({ data }) => {
        // .limit(1) em vez de .single(): se houver linha duplicada de students
        // pro mesmo profile_id, .single() erraria e travaria a home num spinner
        // infinito. order('id') garante escolha determinística e consistente
        // com o _layout. (Bug Jennifer 2026-06-05.)
        const row = data?.[0];
        if (row) {
          setStudentId(row.id);
          setNotifEnabled(row.notification_enabled ?? false);
          setNotifHour(row.notification_hour ?? 7);
        }
      });
  }, [profile]);

  const { routines, activeSession, loading, refetch } = useWorkoutRoutines(studentId ?? '');

  const today = new Date();

  useFocusEffect(useCallback(() => {
    if (studentId) refetch();
  }, [studentId]));

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (!studentId || loading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
      <View style={styles.header}>
        <Text style={styles.greeting}>{
          today.getHours() < 12 ? 'Bom dia' : today.getHours() < 18 ? 'Boa tarde' : 'Boa noite'
        }, {profile?.nickname ?? profile?.full_name?.split(' ')[0]}! 💪</Text>
      </View>

      {activeSession && (
        <TouchableOpacity
          style={styles.activeBanner}
          onPress={() => router.push(`/(student)/workout/${activeSession.planId}` as any)}
        >
          <PulseDot />
          <View style={{ flex: 1 }}>
            <Text style={styles.bannerTitle}>TREINO EM ANDAMENTO</Text>
            <Text style={styles.bannerSub}>
              {activeSession.planName} · {activeSession.logCount} de {activeSession.totalCount} exercícios
            </Text>
          </View>
          <Text style={styles.bannerAction}>Voltar →</Text>
        </TouchableOpacity>
      )}

      <WorkoutCalendar studentId={studentId} />

      <Text style={styles.sectionLabel}>Treinos</Text>

      {routines.length === 0 && (
        <Text style={styles.empty}>
          {profile?.role === 'personal'
            ? 'Você ainda não criou rotinas pra si mesmo.'
            : 'Sua personal ainda não criou rotinas para você.'}
        </Text>
      )}

      {routines.map(routine => {
        const isExpanded = expanded.has(routine.id);
        return (
          <View key={routine.id} style={[styles.routineCard, isExpanded && styles.routineCardOpen]}>
            <TouchableOpacity style={styles.routineHeader} onPress={() => toggle(routine.id)}>
              <View style={{ flex: 1 }}>
                <Text style={styles.routineName}>{routine.name}</Text>
                <Text style={styles.routineMeta}>
                  {routine.start_date ? formatDateShort(routine.start_date) : ''}
                  {routine.start_date && routine.end_date ? ' – ' : ''}
                  {routine.end_date ? formatDateShort(routine.end_date) : ''}
                  {routine.plans.length > 0 ? ` · ${routine.plans.length} treinos` : ''}
                </Text>
              </View>
              <Text style={[styles.chevron, isExpanded && styles.chevronOpen]}>▼</Text>
            </TouchableOpacity>

            {isExpanded && (
              <View style={styles.plansList}>
                {routine.plans.map((plan, idx) => {
                  const isActive = activeSession?.planId === plan.id;
                  return (
                    <TouchableOpacity
                      key={plan.id}
                      style={[
                        styles.planRow,
                        idx < routine.plans.length - 1 && styles.planRowBorder,
                        isActive && styles.planRowActive,
                      ]}
                      onPress={() => router.push(`/(student)/workout/${plan.id}` as any)}
                    >
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          {isActive && <PulseDot />}
                          <Text style={[styles.planName, isActive && styles.planNameActive]}>
                            {plan.name}
                          </Text>
                        </View>
                        <View style={styles.planMeta}>
                          <Text style={styles.planDate}>
                            {isActive
                              ? `${activeSession.logCount} de ${activeSession.totalCount} exercícios`
                              : plan.lastPerformedAt
                              ? `${plan.sessionCount} treino${plan.sessionCount !== 1 ? 's' : ''} realizados · último ${formatDateShort(plan.lastPerformedAt)}`
                              : 'Nunca realizado'}
                          </Text>
                          <Text style={[styles.planAction, isActive && styles.planActionBold]}>
                            {isActive ? 'Continuar →' : 'Ver treino →'}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </View>
        );
      })}

      <Text style={styles.sectionLabel}>Configurações</Text>
      {studentId && (
        <NotificationSettings
          studentId={studentId}
          initialEnabled={notifEnabled}
          initialHour={notifHour}
        />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  greeting: { fontSize: 22, fontWeight: 'bold', color: '#1A1A1A' },
  activeBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#FFF0F2', borderWidth: 1.5, borderColor: '#C8102E44',
    borderRadius: 12, padding: 14, marginBottom: 16,
  },
  pulseDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: '#C8102E', flexShrink: 0 },
  bannerTitle: { fontSize: 11, fontWeight: '700', color: '#C8102E', letterSpacing: 0.5 },
  bannerSub: { fontSize: 12, color: '#666', marginTop: 2 },
  bannerAction: { fontSize: 12, color: '#C8102E', fontWeight: '700' },
  routineCard: { backgroundColor: '#fff', borderRadius: 12, marginBottom: 10, borderWidth: 1.5, borderColor: '#eee', overflow: 'hidden' },
  routineCardOpen: { borderColor: '#C8102E22' },
  routineHeader: { flexDirection: 'row', alignItems: 'center', padding: 14 },
  routineName: { fontSize: 15, fontWeight: '700', color: '#1A1A1A' },
  routineMeta: { fontSize: 11, color: '#888', marginTop: 2 },
  chevron: { fontSize: 14, color: '#aaa', fontWeight: '700' },
  chevronOpen: { color: '#C8102E', transform: [{ rotate: '180deg' }] },
  plansList: { backgroundColor: '#fafafa', borderTopWidth: 1, borderTopColor: '#eee' },
  planRow: { padding: 12, paddingHorizontal: 16 },
  planRowBorder: { borderBottomWidth: 1, borderBottomColor: '#eee' },
  planRowActive: { backgroundColor: '#FFF0F2' },
  planName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  planNameActive: { color: '#C8102E' },
  planMeta: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
  planDate: { fontSize: 11, color: '#888' },
  planAction: { fontSize: 11, color: '#C8102E', fontWeight: '600' },
  planActionBold: { fontWeight: '800' },
  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 8, marginBottom: 8 },
  empty: { textAlign: 'center', color: '#aaa', marginTop: 40 },
});
